import React from 'react';
import {ThemeDefinition} from '@volkswagen-onehub/components-core';

import {
    calcPosition,
    getItemColumns,
    nextItem,
    prevItem,
    width
} from '../helpers';
import {MobileCarouselState} from '../types';

export type TouchMoveRecord = {
    position?: number;
    time: number;
    x: number;
    y: number;
};

type UseTouchHandlersProps = {
    activeSlideIndex: number;
    carouselRef: React.RefObject<HTMLDivElement>;
    itemCount: number;
    hideNextSlide: boolean;
    isWideSlide: boolean;
    direction: ThemeDefinition['direction'];
    onSlideChange?: (activeSlide: number) => void;
    setState: React.Dispatch<React.SetStateAction<MobileCarouselState>>;
    state: MobileCarouselState;
};

export const useTouchHandlers = ({
    activeSlideIndex,
    carouselRef,
    direction,
    hideNextSlide,
    isWideSlide,
    itemCount,
    onSlideChange,
    setState,
    state
}: UseTouchHandlersProps) => {
    const deltaRef = React.useRef<TouchMoveRecord>();
    const isScrollingRef = React.useRef<boolean>();
    const startRef = React.useRef<TouchMoveRecord>();

    const stopPropagation = React.useCallback((e: Event) => {
        e.stopImmediatePropagation();
    }, []);

    // Updated disableScroll that includes the carouselRef logic.
    const disableScroll = React.useCallback(() => {
        const options = {passive: false};
        window.addEventListener('scroll', stopPropagation, options);
        window.addEventListener('touchmove', stopPropagation, options);
        window.addEventListener('wheel', stopPropagation, options);
        document.body.style.overflowY = 'hidden';

        if (!carouselRef.current) return;
        // Remove horizontal scrolling from the carousel element.
        carouselRef.current.style.overflowX = '';
    }, [carouselRef, stopPropagation]);

    // Updated enableScroll that includes the carouselRef logic.
    const enableScroll = React.useCallback(() => {
        window.removeEventListener('scroll', stopPropagation, false);
        window.removeEventListener('touchmove', stopPropagation, false);
        window.removeEventListener('wheel', stopPropagation);
        document.body.style.overflowY = '';

        if (!carouselRef.current) return;
        // Restore horizontal scroll restrictions on the carousel element.
        carouselRef.current.style.overflowX = 'hidden';
    }, [carouselRef, stopPropagation]);

    const handleSlideStart = React.useCallback(
        (e: React.TouchEvent<HTMLElement>) => {
            disableScroll();
            const touches = e.touches[0];
            startRef.current = {
                x: touches.pageX,
                y: touches.pageY,
                position: state.position,
                time: Date.now()
            };
            isScrollingRef.current = undefined;
        },
        [disableScroll, state.position]
    );

    const handleSlideMove = React.useCallback(
        (carouselWidth: number) => (e: React.TouchEvent<HTMLElement>) => {
            if (e.touches.length > 1 || !startRef.current) return;

            const start = startRef.current;
            const touches = e.touches[0];
            const startPosition = start.position || 0;
            let deltaX = touches.pageX - start.x;
            const deltaY = touches.pageY - start.y;

            if (isScrollingRef.current === undefined) {
                isScrollingRef.current = Math.abs(deltaX) < Math.abs(deltaY);
            }

            const isScrolling = isScrollingRef.current;
            if (isScrolling) {
                enableScroll();
                return;
            }

            const firstAndSlidingLeft = activeSlideIndex === 0 && deltaX > 0;
            const lastAndSlidingRight =
                activeSlideIndex === itemCount - 1 && deltaX < 0;
            const resistanceLevel =
                firstAndSlidingLeft || lastAndSlidingRight
                    ? Math.abs(deltaX) / carouselWidth + 1
                    : 1;

            deltaX = deltaX / resistanceLevel;

            const deltaPosition =
                (deltaX / carouselWidth) *
                width(itemCount, getItemColumns(isWideSlide, hideNextSlide));

            deltaRef.current = {
                x: deltaX,
                y: deltaY,
                position: deltaPosition,
                time: Date.now()
            };

            setState(prev => ({
                ...prev,
                position: startPosition + deltaPosition,
                isIntermediatePosition: true
            }));
        },
        [
            activeSlideIndex,
            itemCount,
            isWideSlide,
            hideNextSlide,
            enableScroll,
            setState
        ]
    );

    const handleSlideEnd = React.useCallback(
        (e: React.TouchEvent<HTMLElement>) => {
            enableScroll();
            const delta = deltaRef.current;
            const start = startRef.current;

            if (!delta || !start || isScrollingRef.current) return;

            e.preventDefault();
            e.stopPropagation();

            const {position = 0} = delta;
            const duration = Date.now() - start.time;
            const w = width(
                itemCount,
                getItemColumns(isWideSlide, hideNextSlide)
            );
            const absPositionDelta = Math.abs(position);
            const nextSlideThreshold = w / 2;

            const isValidSlide =
                (duration < 250 && absPositionDelta > w * 0.1) ||
                absPositionDelta > nextSlideThreshold;

            if (isValidSlide) {
                const nextActiveSlide =
                    position > 0
                        ? prevItem(activeSlideIndex)
                        : nextItem(activeSlideIndex, itemCount);

                setState(prev => ({
                    ...prev,
                    position: calcPosition(
                        nextActiveSlide,
                        itemCount,
                        hideNextSlide,
                        isWideSlide,
                        direction
                    ),
                    isIntermediatePosition: false
                }));

                if (onSlideChange && activeSlideIndex !== nextActiveSlide) {
                    onSlideChange(nextActiveSlide);
                }
            } else if (Math.abs(delta.x) > 1) {
                setState(prev => ({
                    ...prev,
                    position: calcPosition(
                        activeSlideIndex,
                        itemCount,
                        hideNextSlide,
                        isWideSlide,
                        direction
                    ),
                    isIntermediatePosition: false
                }));
            }

            startRef.current = undefined;
            deltaRef.current = undefined;
        },
        [
            activeSlideIndex,
            direction,
            enableScroll,
            hideNextSlide,
            isWideSlide,
            itemCount,
            onSlideChange,
            setState
        ]
    );

    return {
        handleSlideEnd,
        handleSlideMove,
        handleSlideStart,
        isScrollingRef,
        state
    };
};
